import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-community";
import { AgCharts as AgChartsComponent } from "ag-charts-react";
import chroma from "chroma-js";

import { formatCurrency } from "fond/utils/currency";

import { getCostRange } from "./costChartUtils";

const BIN_COUNT = 50;
// When a bar is very small, the border/corner-radius can swallow the fill entirely, making it
// invisible. To avoid this, we floor any non-empty bar to at least this fraction of the tallest
// bar's height.
const MIN_DISPLAY_RATIO = 0.08;

interface CostBin {
  // The cost value at the center of this bin's range. It determines where the bar should be drawn
  // on the x-axis.
  cost: number;
  rangeStart: number;
  rangeEnd: number;
  count: number;
  color: string;
}

interface CostDistributionChartProps {
  costs: number[];
  ctsTypeLabel: string | undefined;
}

/**
 * Bins the given cost values into a fixed number of bins, returning an array of CostBin objects.
 *
 */
const binCosts = (costs: number[], domainMin: number, domainMax: number): CostBin[] => {
  if (costs.length === 0) return [];

  // Default to 1 to avoid divide-by-zero when all costs are identical. This will make all costs fall into the first bin.
  const binWidth = (domainMax - domainMin || 1) / BIN_COUNT;
  const colors = chroma.scale([deepPurple[100], deepPurple[900]]).colors(BIN_COUNT);

  const bins: CostBin[] = Array.from({ length: BIN_COUNT }, (_, index) => {
    const rangeStart = domainMin + index * binWidth;
    const rangeEnd = rangeStart + binWidth;
    return { cost: (rangeStart + rangeEnd) / 2, rangeStart, rangeEnd, count: 0, color: colors[index] };
  });

  costs.forEach((cost) => {
    const index = Math.min(Math.floor((cost - domainMin) / binWidth), BIN_COUNT - 1);
    bins[index].count += 1;
  });

  return bins;
};

const CostDistributionChart: React.FC<CostDistributionChartProps> = ({ costs, ctsTypeLabel }) => {
  const chartOptions: AgChartOptions = useMemo(() => {
    const { min: domainMin, max: domainMax } = costs.length > 0 ? getCostRange(costs) : { min: undefined, max: undefined };
    const bins = domainMin !== undefined && domainMax !== undefined ? binCosts(costs, domainMin, domainMax) : [];
    const maxCount = Math.max(1, ...bins.map(({ count }) => count));
    const axisMin = bins.length > 0 ? bins[0].rangeStart : undefined;
    const axisMax = bins.length > 0 ? bins[bins.length - 1].rangeEnd : undefined;

    const displayBins = bins.map((bin) => ({ ...bin, displayCount: bin.count > 0 ? Math.max(bin.count, maxCount * MIN_DISPLAY_RATIO) : 0 }));

    const renderTooltip = (params: { datum: CostBin }) => {
      const datum = params.datum as CostBin;
      return {
        heading: ctsTypeLabel,
        title: `${datum.count} service location${datum.count !== 1 ? "s" : ""} · ${formatCurrency(datum.rangeStart, { notation: "compact" })} - ${formatCurrency(datum.rangeEnd, { notation: "compact" })}`,
        data: [],
        symbol: { marker: { fill: datum.color, stroke: datum.color } },
      };
    };

    const series: AgBarSeriesOptions = {
      type: "bar",
      direction: "vertical",
      xKey: "cost",
      yKey: "displayCount",
      xName: ctsTypeLabel,
      stroke: "#fff",
      strokeWidth: 1,
      cornerRadius: 3,
      itemStyler: (params) => ({ fill: (params.datum as CostBin).color }),
      tooltip: {
        range: "nearest",
        renderer: renderTooltip,
      },
    };

    return {
      data: displayBins,
      series: [series],
      padding: { top: 0, bottom: 24 },
      axes: {
        x: {
          type: "number",
          position: "bottom",
          min: axisMin,
          max: axisMax,
          label: { fontSize: 9, formatter: (params) => formatCurrency(params.value, { notation: "compact" }) },
          gridLine: { enabled: false },
        },
        y: {
          type: "number",
          position: "left",
          gridLine: { enabled: false },
          line: { enabled: false },
          tick: { enabled: false },
          label: { enabled: false },
        },
      },
    };
  }, [costs, ctsTypeLabel]);

  return (
    <Box flex={3} minHeight={0} display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
        <Typography fontSize={12} fontWeight={700}>
          Distribution of service locations
        </Typography>
        <Typography fontSize={11} color="text.secondary">
          Hover on bars for detail
        </Typography>
      </Box>
      <Box flex={1} minHeight={0}>
        <AgChartsComponent options={chartOptions} style={{ height: "100%", width: "100%" }} />
      </Box>
    </Box>
  );
};

export default CostDistributionChart;
